import { useCallback, useMemo } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import type {
  DataTableSelectionState,
  TableState,
} from 'components/data-table/types';
import type { SelectionSource } from 'components/create-area-panel/create-area-types';
import { activeDatasetTabAtom } from '../atoms';
import { datasetRegistry } from '../datasets';
import type { DatasetConfig } from '../types';
import { workspaceNeedsMapAtom } from 'components/create-area-panel/atoms';
import { areaSelectionsAtom, createAreaOpenAtom } from './atoms';
import { EMPTY_TABLE_SELECTION, nextAreaSelection } from './utils';

type Row = Record<string, unknown>;

/** Any results table with checkboxes can seed an area, so the hint names them all. */
const EMPTY_SELECTION_HINT =
  'Check rows in the Wells, Areas, or any other dataset table to build an area from them.';

/** What a results table needs to offer checkboxes for Create New Area. */
export interface TableAreaSelection {
  selection: DataTableSelectionState;
  onSelectionChange: (state: TableState) => void;
}

/**
 * Checkboxes on a results table whose rows can seed a new area. Null for any other
 * table, which then renders as it always has.
 *
 * @param {DatasetConfig} config - The table's dataset.
 * @param {Row[]} pageRows - The rows on screen.
 * @param {Function} rowKey - The table's `getRowId`.
 * @return {TableAreaSelection | null} The controlled selection, or null.
 */
export const useTableAreaSelection = (
  config: DatasetConfig,
  pageRows: Row[],
  rowKey: (row: Row) => string
): TableAreaSelection | null => {
  const [selections, setSelections] = useAtom(areaSelectionsAtom);
  const selectionConfig = config.areaSelection;
  const enabled = !!selectionConfig;

  const onSelectionChange = useCallback(
    (state: TableState) => {
      if (!selectionConfig) return;
      setSelections((current) => ({
        ...current,
        [config.id]: nextAreaSelection({
          previous: current[config.id],
          selection: state.selection,
          pageRows,
          rowKey,
          config: selectionConfig,
        }),
      }));
    },
    [config.id, pageRows, rowKey, selectionConfig, setSelections]
  );

  if (!enabled) return null;
  return {
    selection: selections[config.id]?.selection ?? EMPTY_TABLE_SELECTION,
    onSelectionChange,
  };
};

/**
 * The rows the panel builds from: the active tab's checked rows when that dataset
 * can seed an area, else the first dataset with any checked, else an empty Areas
 * selection so the panel can say nothing is selected.
 *
 * @return {SelectionSource | undefined} The selection, or undefined when no
 * dataset can seed an area.
 */
export const useActiveSelectionSource = (): SelectionSource | undefined => {
  const selections = useAtomValue(areaSelectionsAtom);
  const activeTab = useAtomValue(activeDatasetTabAtom);

  return useMemo(() => {
    const candidates = Object.values(datasetRegistry).filter(
      (config) => config.areaSelection
    );
    const active = candidates.find((config) => config.id === activeTab);
    const withRows = candidates.find(
      (config) => (selections[config.id]?.rows.length ?? 0) > 0
    );
    const chosen =
      (active && (selections[active.id]?.rows.length ?? 0) > 0
        ? active
        : withRows) ??
      active ??
      candidates.find((config) => config.areaSelection?.kind === 'areas') ??
      candidates[0];
    if (!chosen?.areaSelection) return undefined;

    return {
      kind: chosen.areaSelection.kind,
      noun: chosen.label,
      singular: chosen.areaSelection.singular,
      emptyHint: EMPTY_SELECTION_HINT,
      geometry: chosen.areaSelection.geometry,
      rows: selections[chosen.id]?.rows ?? [],
    };
  }, [selections, activeTab]);
};

/**
 * Whether the Create New Area panel has the map: open, on a method drawn or
 * picked on it. Map Query's own click tools (popups, the AOI draw) stand down.
 *
 * @return {boolean} True while the panel works on the map.
 */
export const useCreateAreaHasMap = (): boolean => {
  const open = useAtomValue(createAreaOpenAtom);
  const needsMap = useAtomValue(workspaceNeedsMapAtom);
  return open && needsMap;
};
